import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ExecutionListItem } from '../lib/api';

const STATUS_FILTERS = ['all', 'RUNNING', 'SUCCEEDED', 'FAILED'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

function fmtDuration(start: number | null, stop: number | null): string {
  if (!start) return '—';
  const end = stop ?? Date.now();
  const sec = Math.floor((end - start) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function fmtTime(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString();
}

function statusClass(status: ExecutionListItem['status']): string {
  switch (status) {
    case 'RUNNING':
      return 'bg-amber-100 text-amber-900';
    case 'SUCCEEDED':
      return 'bg-forest-100 text-forest-800';
    case 'FAILED':
    case 'TIMED_OUT':
    case 'ABORTED':
      return 'bg-red-100 text-red-900';
    default:
      return 'bg-parchment-200 text-parchment-800';
  }
}

export function PipelinePage() {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<string | null>(null);

  const executionsQuery = useQuery({
    queryKey: ['executions', filter],
    queryFn: () => api.listExecutions(filter === 'all' ? undefined : filter, 50),
    refetchInterval: 5000,
  });

  const detailQuery = useQuery({
    queryKey: ['execution', selected],
    queryFn: () => api.getExecution(selected!),
    enabled: !!selected,
    refetchInterval: selected ? 5000 : false,
  });

  return (
    <div>
      <h2 className="text-3xl font-bold mb-2">Pipeline</h2>
      <p className="text-parchment-700 mb-6">
        Live Step Functions executions. Each video traces safety → research → script
        → voice → b-roll → assembly → publish.
      </p>

      <div className="flex gap-2 mb-6">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-button text-sm font-mono transition-shadow ${
              filter === s
                ? 'bg-forest-600 text-parchment-50 shadow-neumorphic-inset'
                : 'bg-parchment-50 text-parchment-800 shadow-neumorphic-sm'
            }`}
          >
            {s.toLowerCase()}
          </button>
        ))}
      </div>

      {executionsQuery.isLoading && (
        <p className="text-sm font-mono text-parchment-700">loading…</p>
      )}
      {executionsQuery.isError && (
        <p className="text-sm font-mono text-red-700">api unreachable</p>
      )}
      {executionsQuery.data && executionsQuery.data.items.length === 0 && (
        <div className="card-neumorphic text-center text-parchment-700 py-12">
          <p className="font-mono text-sm">no executions match</p>
        </div>
      )}

      <ul className="space-y-2">
        {executionsQuery.data?.items.map((e: ExecutionListItem) => (
          <li
            key={e.execution_arn}
            className="card-neumorphic !p-4 cursor-pointer hover:shadow-neumorphic-md transition-shadow"
            onClick={() => setSelected(e.execution_arn)}
          >
            <div className="flex justify-between items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="font-medium font-mono text-sm truncate">{e.name}</p>
                <p className="text-xs font-mono text-parchment-700 mt-1">
                  started {fmtTime(e.start_date)} · duration {fmtDuration(e.start_date, e.stop_date)}
                </p>
              </div>
              <span
                className={`text-xs font-mono rounded-button px-2 py-1 ${statusClass(
                  e.status,
                )}`}
              >
                {e.status.toLowerCase()}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {selected && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center p-4 z-50"
          onClick={() => setSelected(null)}
        >
          <div
            className="card-neumorphic !p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-heading font-semibold text-xl">Execution Detail</h3>
              <button
                onClick={() => setSelected(null)}
                className="text-parchment-700 hover:text-parchment-900"
                aria-label="close"
              >
                ✕
              </button>
            </div>
            {detailQuery.isLoading && (
              <p className="font-mono text-sm text-parchment-700">loading…</p>
            )}
            {detailQuery.data && (
              <>
                <p className="font-mono text-xs text-parchment-700 mb-4 break-all">
                  {detailQuery.data.execution_arn}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div>
                    <p className="text-xs font-mono uppercase text-parchment-700">status</p>
                    <p
                      className={`mt-1 text-sm font-mono inline-block px-2 py-1 rounded-button ${statusClass(
                        detailQuery.data.status,
                      )}`}
                    >
                      {detailQuery.data.status.toLowerCase()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase text-parchment-700">duration</p>
                    <p className="mt-1 text-sm font-mono">
                      {fmtDuration(detailQuery.data.start_date, detailQuery.data.stop_date)}
                    </p>
                  </div>
                </div>
                {detailQuery.data.error && (
                  <div className="rounded-button bg-red-100 text-red-900 px-4 py-3 mb-4 text-sm">
                    <p className="font-mono font-semibold">{detailQuery.data.error}</p>
                    {detailQuery.data.cause && (
                      <p className="font-mono text-xs mt-1 break-words">
                        {detailQuery.data.cause}
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs font-mono uppercase text-parchment-700 mb-2">
                  history (most recent first)
                </p>
                <ul className="space-y-1">
                  {detailQuery.data.events.slice(0, 30).map((ev) => (
                    <li
                      key={ev.id}
                      className="font-mono text-xs text-parchment-800 border-b border-parchment-200 pb-1"
                    >
                      <span className="text-parchment-600">
                        {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString() : '—'}
                      </span>{' '}
                      <span className="font-semibold">{ev.type}</span>{' '}
                      {ev.state_entered && (
                        <span className="text-forest-700">→ {ev.state_entered}</span>
                      )}
                      {ev.state_exited && (
                        <span className="text-parchment-600">← {ev.state_exited}</span>
                      )}
                      {ev.task_failed && (
                        <span className="text-red-700"> failed: {ev.task_failed}</span>
                      )}
                      {ev.lambda_failed && (
                        <span className="text-red-700"> lambda: {ev.lambda_failed}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
